import React, { useEffect, useState } from 'react'
import { useTransition, animated } from 'react-spring'
import { ProgressBar } from '@blueprintjs/core'
import { Dialog, Flex, IconButton, MapFloorSelector } from '@ui'
import { useFloor } from '@providers/mapProvider'
import { IAssignmentPlan, IFloor, Role } from '@api/types'
import APWidget from '../APWidget'
import css from './adminFloorPlan.module.scss'
import BookingCalendarPopup from '../BookingCalendarPopup/BookingCalendarPopup'
import { useBookingDesk } from '@providers'
import EditFloorMap from 'components/EditFloorMap'
import { useBookingCalendar } from 'components/BookingCalendarPopup/hooks'
import { isFinder } from '@utils'

interface ToolbarProps {
  splitScreen: boolean
  pinLeftNav?: boolean
  onApChange: (ap: IAssignmentPlan) => void
  onFloorChange: (floor: IFloor) => void
  searchComponent: React.ReactNode
  bookDeskComponent: React.ReactNode
  filtersComponent: React.ReactNode
  role: Role | undefined
}

function ShadowWrapper({ items }: { items: React.ReactNode[] }) {
  if (!Boolean(items.length)) return null

  return (
    <div className="toolbar-shadow-wrapper">
      {items.map((i, index, arr) => {
        return index === arr.length - 1 ? (
          <div key={`shadow_wrapper_${index}`}>{i}</div>
        ) : (
          <React.Fragment key={`shadow_wrapper_${index}`}>
            <div>{i}</div>
            <div />
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default function Toolbar({
  splitScreen,
  pinLeftNav,
  onApChange,
  onFloorChange,
  searchComponent,
  bookDeskComponent,
  filtersComponent,
  role,
}: ToolbarProps) {
  const [isOpen, setOpen] = useState(false)

  const floor = useFloor()
  const FORMAT = 'E, MMM dd'
  const {
    date: { fromDate, toDate },
  } = useBookingDesk()
  const [updatedDates, setUpdatedDates] = useState({ fromDate: fromDate, toDate: toDate })
  useEffect(() => {
    floor.reloadMap?.(fromDate, toDate)
    setUpdatedDates({ fromDate: fromDate, toDate: toDate })
  }, [fromDate, toDate])

  const timezone = floor.map?.building.timezone || ''
  const { updateBookingDate } = useBookingCalendar()
  return (
    <>
      <FloorPlanProgressBar progress={floor.progress} pinLeftNav={pinLeftNav} />
      <Flex
        align="center"
        justify="space-between"
        style={{
          transition: 'width 0.3s',
          position: 'relative',
          height: 50,
          width: splitScreen ? 'calc(100% - 300px)' : '100%',
          zIndex: 19,
          top: 19,
        }}>
        <Flex align="center">
          <MapFloorSelector value={floor.floorId as string} onChange={onFloorChange} />
          <div className={css.separator} />
          {(floor.isBookDeskPage || floor.isBookMeetingRoomPage) && (
            <>
              <BookingCalendarPopup
                value={`${updatedDates.fromDate.format(FORMAT)} - ${(
                  updatedDates.toDate || updatedDates.fromDate
                ).format(FORMAT)} ${updatedDates.fromDate.zoneAbbr()}`}
                title="Select Date Range"
                timezone={timezone}
                floor={floor}
                updateHandler={(dates) => {
                  updateBookingDate({ fromDate: dates[0], toDate: dates[1] || dates[0] })
                  floor.reloadMap?.(dates[0], dates[1] || dates[0])
                }}>
                <div></div>
              </BookingCalendarPopup>
              <div className={css.separator} />
            </>
          )}
          <ShadowWrapper items={[bookDeskComponent]} />
          <ShadowWrapper items={[filtersComponent]} />
          <div className={css.separator} />
          {!isFinder(role) && <APWidget value={floor.apId} onChange={onApChange} />}
          <div className={css.separator} />
          <IconButton
            className={css.pencil_icon}
            round={false}
            iconName="pencil"
            onClick={() => setOpen(true)}
          />
          <div className={css.separator} />
        </Flex>
        <div className={css.search_box}>
          <ShadowWrapper items={[searchComponent]} />
        </div>
        <Dialog
          isOpen={isOpen}
          isCloseButtonShown
          onClose={() => setOpen(false)}
          body={<EditFloorMap />}
        />
      </Flex>
    </>
  )
}

function FloorPlanProgressBar({
  progress,
  pinLeftNav,
}: {
  progress: number
  pinLeftNav?: boolean
}) {
  const transitions = useTransition(progress !== 100, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  return (
    <span>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <animated.div
              key={key}
              style={{
                ...props,
                position: 'fixed',
                zIndex: 10,
                bottom: 24,
                top: 50,
                left: pinLeftNav ? 270 : 70,
                right: 0,
                height: 3,
              }}>
              <ProgressBar className="progress-thin" value={progress / 100} stripes={false} />
            </animated.div>
          )
      )}
    </span>
  )
}
